import React, { useMemo } from 'react'
import { Select, type SelectOption } from 'sutra/components/Select/Select'
import { brand } from 'sutra/tokens/colors'

import { DetectedIssueIcon } from 'features/trend-detection/components/IssueShared'
import { GapMeasurementIcon } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'
import type { AgentType } from 'features/trend-detection/types'

const AGENT_TYPE_LABEL: Record<AgentType, string> = {
  issue_detection: 'Issue Agent',
  benchmarking: 'Benchmarking Agent',
  general: 'Reports Agent',
}

const ICON_COLOR = brand.stillNight[800]
const ICON_SIZE = 16

// Same icons QuickCreateCards uses for these types — Reports has no icon of
// its own yet, so it reuses the Issue icon (same precedent AgentTypeStep set
// before it was deleted).
const AGENT_TYPE_ICON: Record<AgentType, React.ReactElement> = {
  issue_detection: (
    <DetectedIssueIcon color={ICON_COLOR} width={ICON_SIZE} height={12} />
  ),
  benchmarking: (
    <GapMeasurementIcon
      direction="leading"
      color={ICON_COLOR}
      width={ICON_SIZE}
      height={ICON_SIZE}
    />
  ),
  general: (
    <DetectedIssueIcon color={ICON_COLOR} width={ICON_SIZE} height={12} />
  ),
}

export interface AgentTypeFieldProps {
  value: AgentType
  onChange: (type: AgentType) => void
  /** Types selectable here, filtered upstream by feature flags — mirrors
   * the deleted AgentTypeStep's `availableTypes` contract: never falls back
   * to "show everything", which would leak flag-gated types. */
  availableTypes: AgentType[]
  /** True in the edit-mode form — an agent's type isn't mutable after
   * creation, and neither PATCH endpoint accepts it. */
  disabled?: boolean
}

/** Agent type as a single field in the unified form — replaces the old
 * step-1 type-selection screen. */
export const AgentTypeField: React.FC<AgentTypeFieldProps> = ({
  value,
  onChange,
  availableTypes,
  disabled,
}) => {
  const options: SelectOption<AgentType>[] = useMemo(
    () =>
      availableTypes.map((type) => ({
        value: type,
        label: AGENT_TYPE_LABEL[type],
      })),
    [availableTypes],
  )

  return (
    <Select
      value={value}
      onChange={(type) => onChange(type)}
      options={options}
      triggerIcon={AGENT_TYPE_ICON[value]}
      disabled={disabled}
      size="md"
    />
  )
}
